import React from 'react';
import { Brodcrumb } from '../../components/ui/UtilsLayout';
import { iconsPath, imagePath, videoPath } from '../../utils/imagePath';
import { BackgroundWithCurve, BulletSection, CaseStudiesImageCard, CaseStudyHeading, Section } from '../../utils/CaseStudiesComponents';

// CaseStudy Component
const CorpusCodex = () => {
  return (
    <div className="flex justify-center px-5 md:px-10 lg:px-16  items-center gap-6 text-white pb-16 relative">
      <BackgroundWithCurve />
      <div className="max-w-[1200px] w-full  pt-24 md:pt-32 relative z-10">
        {/* Breadcrumb Navigation */}
        <Brodcrumb
          data={{
            name: 'Corpus Codex',
            item: [
              { name: 'Industry Use Cases', path: '/all-industries' },
              {
                name: 'Education & Training',
                path: '/industries/education-training',
              },
            ],
          }}
        />

        <div className="my-10 flex flex-col w-full gap-10">
          {/* Header Section with Logo */}
          <div className="flex gap-3 md:gap-5 lg:gap-10">
            <div className="flex justify-center items-center  bg-gradient-to-t from-[#525E77] to-[#2D364A] shadow-[0px_0px_4px_1px_rgba(0,0,0,0.25)] h-16 w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 rounded-lg">
              {/* Company Logo */}
              {imagePath?.ImmersiveAnatomyLogo ? (
                <img
                  className="h-12 w-12 md:h-12 md:w-12 lg:h-16 lg:w-16 object-contain"
                  src={imagePath?.ImmersiveAnatomyLogo}
                  alt="Immersive Anatomy Logo"
                />
              ) : (
                <span className="text-gray-400 text-xs">No Logo</span>
              )}
            </div>

            {/* Case Study Heading */}
            <div className="flex flex-col gap-2 md:gap-5 justify-center">
              <CaseStudyHeading
                title="Immersive Anatomy Research"
                desc="AR+MR Anatomy Education Platform"
              />
            </div>
          </div>

          <div className="relative">
            {/* Video Container - Responsive aspect ratio */}
            <div className="relative w-full aspect-video bg-[#31333A] shadow-[0px_0px_21px_4px_rgba(0,0,0,0.25)] flex justify-center items-center ">
              {/* Play Icon */}
              {videoPath?.ImmersiveAnatomyResearch && (
                <video
                  src={videoPath.ImmersiveAnatomyResearch}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}

              {/* {iconsPath?.QRIcon && (
                <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 lg:-top-8 lg:-right-8 bg-[#fff] p-2 md:p-3 rounded-[10px]">
                  <img
                    src={iconsPath.QRIcon}
                    alt="QR Code"
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24"
                  />
                </div>
              )} */}
            </div>
          </div>

          {/* Case Study Description */}
          <Section
            title="Overview"
            subtitle="Spatial Grid developed an AR and MR–based anatomy learning platform designed to transform how 
            medical students and educators explore the human body. Built after extensive discussions with 
            anatomy experts, the project reimagines traditional anatomy education by making complex structures, 
            systems, and clinical concepts fully interactive and immersive. This platform provides a portable, highly 
            engaging alternative to fixed anatomy hardware—enabling deep, accessible, and research-connected 
            anatomical learning in classrooms, labs, clinics, and remote environments."
          />
          <BulletSection
            title="Objective"
            introText="The goal was to create a next-generation anatomy education tool that:"
            bulletPoints={[
              'Allows students to explore all major human body systems in immersive 3D using AR and MR',
              'Recreates real clinical scenarios through past case studies and diagnostic walkthroughs',
              'Integrates research directly into the experience through the Model Context Protocol (MCP), providing instant access to papers, publications, and journals connected to each organ or system',
              'Functions as a flexible, scalable software platform that extends beyond static teaching tools',
            ]}
            closingText="The solution needed to be mobile, collaborative, and adaptable to evolving curriculum requirements."
          />

          <Section
            title="Solution"
            subtitle="Immersive AR/MR Anatomy Exploration"
            content={[
              'Students can view, manipulate, and walk around detailed 3D anatomical structures at real scale. Layer-by-layer controls allow for isolating systems, examining organs, and visualizing anatomical relationships with precision and clarity.',
              'Clinical Case Study Integration The platform incorporates real-world context by presenting historical diagnoses, pathological variations, and clinical reasoning workflows, helping learners connect anatomy to applied medical practice.',
              'MCP-Powered Research Access Using the Model Context Protocol, each anatomical element serves as an entry point to curated academic literature. Users can instantly pull relevant research papers, journals, and published studies into their learning process.',
            ]}
          />

          {/* Image Grid Section */}
          <div className="flex flex-col gap-3">
            <div className="flex md:flex-row flex-col gap-10 md:gap-20 w-full">
              <CaseStudiesImageCard
                image={imagePath?.Anatomy1}
                title="AR/MR Experience Interface"
              />
              <CaseStudiesImageCard
                image={imagePath?.Anatomy2}
                title="AR/MR Medical Learning an Research"
              />
            </div>
          </div>

          <BulletSection
            title="MVP Development"
            introText="The first MVP focuses on delivering:"
            bulletPoints={[
              'Four to five interactive views representing different human anatomy systems',
              'One specialized organ module with detailed, layered visualization',
              'A set of clinical case studies linked to that organ',
              'A sample research dataset demonstrating MCP integration',
            ]}
            closingText="This phased release structure enables validation of core functionality and smooth expansion to more advanced modules."
          />

          {/* Impact Section */}
          <div className="flex flex-col gap-3">
            <Section
              title="Impact"
              content="This immersive platform modernizes medical anatomy education by combining spatial computing with clinical accuracy and research connectivity. It enhances comprehension, increases engagement, and supports the flexibility educators need to integrate advanced digital tools into their curriculum. With future expansion into surgical workflows, medical procedures, and simulation-based assessments, the solution positions Spatial Grid at the forefront of immersive medical learning."
            />
          </div>
        </div>
      </div>
    </div>
  );
};
<CaseStudyHeading />;
<CaseStudiesImageCard />;
<BulletSection/>

export default CorpusCodex;
